import { useCallback, useContext, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Grid from '@material-ui/core/Grid'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'

import { i18n } from '../../translate/i18n'

import { AuthContext } from '../../context/Auth/AuthContext'
import useSettings from '../../hooks/useSettings'

import { ButtonPrimary } from '../../components/ButtonPrimary'
import { InputFieldText } from '../../components/InputFieldText'
import { useStyles } from './style'

// Remova os imports do topo do arquivo e use os caminhos diretos no <img>
import { IconButton, Tooltip, useTheme } from '@material-ui/core'

import iconAppStore from '../../assets/app-store.png'
import iconPlayStore from '../../assets/play-store.png'
import iconWindowsWhite from '../../assets/windows-branco.png'
import iconWindowsDark from '../../assets/windows.png'
import ConfirmationModal from '../../components/ConfirmationModal'
import AlertDialog from '../../components/AlertDialog'
import toastError from '../../errors/toastError'
import api from '../../services/api'
const supportLink = 'https://api.whatsapp.com/send?phone=556740421069'

const ACCESS_RECOVERY_QUERY_PARAM = 'accessRecoveryCode'

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const Login = () => {
  const classes = useStyles()
  const theme = useTheme()
  const { getPublicSetting } = useSettings()

  const [user, setUser] = useState({ email: '', password: '' })
  const [allowSignup, setAllowSignup] = useState(false)
  const [showLoginAccessWarning, setShowLoginAccessWarning] = useState(false)
  const [loginAccessWarningModalOpen, setLoginAccessWarningModalOpen] =
    useState(false)
  const [loginAccessWarningErrorCode, setLoginAccessWarningErrorCode] =
    useState('')
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null)
  const [installHelpOpen, setInstallHelpOpen] = useState(false)
  const [, setRecoveryModalOpen] = useState(false)

  const [recoveryConfirmOpen, setRecoveryConfirmOpen] = useState(false)
  const [recoveryRequestLoading, setRecoveryRequestLoading] = useState(false)
  const [, setRecoveryLinkError] = useState('')
  const [recoveryStatusMessage, setRecoveryStatusMessage] = useState('')

  const { handleLogin, handleAccessRecoveryLogin } = useContext(AuthContext)

  const windowsIcon =
    theme.palette.type === 'dark' ? iconWindowsDark : iconWindowsWhite

  const sanitizeCredentialValue = value => value.replace(/\s+/g, '')

  const handleOpenRecoveryModal = useCallback(() => {
    setRecoveryModalOpen(true)
  }, [])

  const handleChangeInput = event => {
    const { name, value } = event.target
    const sanitizedValue =
      name === 'email' || name === 'password'
        ? sanitizeCredentialValue(value)
        : value

    if (name === 'email') {
      setRecoveryLinkError('')
      setRecoveryStatusMessage('')
    }

    if (showLoginAccessWarning) {
      setShowLoginAccessWarning(false)
    }

    if (loginAccessWarningModalOpen) {
      setLoginAccessWarningModalOpen(false)
    }

    setUser(prevUser => ({ ...prevUser, [name]: sanitizedValue }))
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const sanitizedUser = {
      ...user,
      email: sanitizeCredentialValue(user.email),
      password: sanitizeCredentialValue(user.password)
    }

    try {
      setShowLoginAccessWarning(false)
      setLoginAccessWarningModalOpen(false)
      setLoginAccessWarningErrorCode('')
      await handleLogin(sanitizedUser)
    } catch (err) {
      const errorCode = err?.response?.data?.error

      if (
        errorCode === 'ERR_LOGIN_ACCESS_DENIED' ||
        errorCode === 'ERR_INVALID_CREDENTIALS'
      ) {
        setShowLoginAccessWarning(true)
      }

      if (
        errorCode === 'ERR_USER_INACTIVE' ||
        errorCode === 'ERR_OUT_OF_WORK_SCHEDULE'
      ) {
        setLoginAccessWarningErrorCode(errorCode)
        setLoginAccessWarningModalOpen(true)
      }
    }
  }

  const handleInstallClick = async () => {
    if (!deferredInstallPrompt) {
      setInstallHelpOpen(true)
      return
    }

    deferredInstallPrompt.prompt()

    const { outcome } = await deferredInstallPrompt.userChoice

    if (outcome === 'accepted') {
      setDeferredInstallPrompt(null)
    }
  }

  const handleRequestAccessRecovery = async () => {
    if (!isValidEmail(user.email)) {
      return
    }

    setRecoveryRequestLoading(true)
    setRecoveryLinkError('')

    try {
      const { data } = await api.post('/auth/access-recovery/request', {
        email: user.email
      })

      setRecoveryStatusMessage(data.message || 'Link de recuperação enviado.')
    } catch (err) {
      setRecoveryLinkError(
        'Nao foi possível enviar o link de recuperação agora.'
      )
      toastError(err)
    } finally {
      setRecoveryRequestLoading(false)
      setRecoveryConfirmOpen(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    getPublicSetting('allowSignup')
      .then(data => {
        if (isMounted) setAllowSignup(data === 'enabled')
      })
      .catch(error => {
        console.log('Error reading setting', error)
      })

    const handleBeforeInstallPrompt = event => {
      event.preventDefault()
      setDeferredInstallPrompt(event)
    }

    const handleAppInstalled = () => {
      setDeferredInstallPrompt(null)
      setInstallHelpOpen(false)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    if (localStorage.getItem('outOfScheduleWarning') === 'true') {
      setLoginAccessWarningErrorCode('ERR_OUT_OF_WORK_SCHEDULE')
      setLoginAccessWarningModalOpen(true)
      localStorage.removeItem('outOfScheduleWarning')
    }

    return () => {
      isMounted = false
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      )
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const recoveryCode = new URLSearchParams(window.location.search).get(
      ACCESS_RECOVERY_QUERY_PARAM
    )

    if (!recoveryCode) {
      return
    }

    let isMounted = true

      ; (async () => {
        try {
          if (isMounted) {
            setRecoveryStatusMessage('Concluindo recuperação de acesso...')
            setShowLoginAccessWarning(false)
          }
          await handleAccessRecoveryLogin(recoveryCode)
        } catch (err) {
          if (!isMounted) {
            return
          }

          window.history.replaceState({}, document.title, '/login')
          setRecoveryStatusMessage(
            'Não foi possível concluir a recuperação de acesso.'
          )
          toastError(err)
        }
      })()

    return () => {
      isMounted = false
    }
  }, [handleAccessRecoveryLogin])

  useEffect(() => {
    if (!isValidEmail(user.email)) {
      setRecoveryLinkError('')
      setRecoveryStatusMessage('')
      setRecoveryConfirmOpen(false)
    }
  }, [user.email])

  useEffect(() => {
    if (user.password) {
      setRecoveryConfirmOpen(false)
    }
  }, [user.password])

  const shouldShowRecoveryLink = isValidEmail(user.email) && !user.password

  return (
    <div className={classes.container}>
      <div className={classes.brand}>
        <div className={classes.brandContent}>
          <img className={classes.logo} alt="" />
        </div>
        {/* <img className={classes.adImg} /> */}
      </div>

      <div className={classes.login}>
        <div className={classes.formContent}>
          <img className={classes.logoForm} alt="" />
          <h2 className={classes.title}>{i18n.t('login.title')}</h2>
          {showLoginAccessWarning && (
            <div className={classes.loginWarningBox}>
              <Typography className={classes.loginWarningTitle}>
                Não foi possível realizar o acesso
              </Typography>
              <Typography className={classes.loginWarningText}>
                Verifique suas credenciais ou contate seu gestor. O acesso pode
                estar restrito por horário ou status da conta.
              </Typography>
            </div>
          )}
          <form noValidate onSubmit={handleSubmit} className={classes.form}>
            <div className={classes.inputWrapper}>
              <label className={classes.label}></label>
              <InputFieldText
                label={i18n.t('login.form.email')}
                required
                id="email"
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
                className={classes.inputText}
              />
            </div>

            <div className={classes.inputWrapper}>
              <InputFieldText
                label={i18n.t('login.form.password')}
                required
                name="password"
                type="password"
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
                className={classes.inputText}
                size="small"
              />
            </div>

            <div className={classes.inputWrapper}>
              <ButtonPrimary
                type="submit"
                fullWidth
                className={classes.submit}
                label={
                  showLoginAccessWarning
                    ? 'Tentar Novamente'
                    : i18n.t('login.buttons.submit')
                }
              />
            </div>

            <div className={classes.loginLinksRow}>
              <Link
                component="button"
                type="button"
                onClick={() => setRecoveryConfirmOpen(true)}
                className={classes.loginHelperLink}
                disabled={recoveryRequestLoading}
              >
                Recuperar Acesso
              </Link>
              <Link
                href={supportLink}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.loginHelperLink}
                variant="body2"
              >
                Suporte
              </Link>
            </div>

            {recoveryStatusMessage && (
              <Typography className={classes.recoveryStatusMessage}>
                {recoveryStatusMessage}
              </Typography>
            )}

            {allowSignup && (
              <Grid container>
                <Grid item>
                  <Link
                    href="#"
                    variant="body2"
                    component={RouterLink}
                    to="/signup"
                  >
                    {i18n.t('login.buttons.register')}
                  </Link>
                </Grid>
              </Grid>
            )}
          </form>
        </div>
        <div className={classes.buttonsDownload}>
          <div>
            <Tooltip title="Google Play Store">
              <IconButton
                className={classes.iconDownload}
                component="a"
                onClick={handleInstallClick}
              >
                <img
                  src={iconPlayStore}
                  alt="Play Store"
                  className={classes.storeIcon}
                />
              </IconButton>
            </Tooltip>

            {/* App Store */}
            <Tooltip title="App Store">
              <IconButton
                className={classes.iconDownload}
                component="a"
                onClick={handleInstallClick}
              >
                <img
                  src={iconAppStore}
                  alt="App Store"
                  className={classes.storeIcon}
                />
              </IconButton>
            </Tooltip>

            {/* Windows / PWA Desktop */}
            <Tooltip title="Windows / Desktop">
              <IconButton
                className={classes.iconDownload}
                component="a"
                onClick={handleInstallClick}
              >
                <img
                  src={windowsIcon}
                  alt="Windows"
                  className={classes.storeIcon}
                />
              </IconButton>
            </Tooltip>
          </div>
          <span>Clique no ícone para instalar o aplicativo </span>
        </div>
      </div>

      {/* <Dialog
        open={recoveryConfirmOpen}
        onClose={() => setRecoveryConfirmOpen(false)}
        aria-labelledby="confirm-recovery-dialog-title"
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.recoveryDialogPaper }}
      >
        <DialogTitle
          id="confirm-recovery-dialog-title"
          className={classes.dialogTitle}
        >
          Confirmar recuperação
        </DialogTitle>
        <DialogContent className={classes.recoveryDialogContent}>
          <Typography variant="body2" className={classes.description}>
            Deseja solicitar a recuperação de acesso para o e-mail informado?
          </Typography>
        </DialogContent>
        <DialogActions>
          <ButtonSecondary
            onClick={() => setRecoveryConfirmOpen(false)}
            label="Cancelar"
          />
          <ButtonPrimary
            onClick={handleRequestAccessRecovery}
            disabled={recoveryRequestLoading}
            label={recoveryRequestLoading ? 'Enviando...' : 'Confirmar'}
          />
        </DialogActions>
      </Dialog> */}

      <ConfirmationModal
        title="Confirmar recuperação"
        open={recoveryConfirmOpen}
        onClose={() => setRecoveryConfirmOpen(false)}
        onConfirm={handleRequestAccessRecovery}
      >
        <Typography variant="body2" className={classes.description}>
          Deseja solicitar a recuperação de acesso para o e-mail informado?
          {recoveryRequestLoading && ' Enviando...'}
        </Typography>
      </ConfirmationModal>

      <ConfirmationModal
        title="Instalar WeCloud"
        open={installHelpOpen}
        onClose={() => setInstallHelpOpen(false)}
      ></ConfirmationModal>

      {/* <Dialog
        open={installHelpOpen}
        onClose={() => setInstallHelpOpen(false)}
        aria-labelledby="install-wecloud-dialog-title"
        maxWidth="xs"
      >
        <DialogTitle
          id="install-wecloud-dialog-title"
          className={classes.dialogTitle}
        >
          Instalar WeCloud
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" className={classes.description}>
            Se o navegador não exibir a instalação automaticamente, abra o menu
            do navegador e escolha a opção de instalar este aplicativo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <ButtonPrimary
            onClick={() => setInstallHelpOpen(false)}
            label="Fechar"
          />
        </DialogActions>
      </Dialog> */}

      <ConfirmationModal
        title="Instalar WeCloud"
        open={installHelpOpen}
        onClose={() => setInstallHelpOpen(false)}
        onConfirm={() => setInstallHelpOpen(false)} // Apenas fecha ou executa ação de instalação
      >
        <Typography variant="body2" className={classes.description}>
          Se o navegador não exibir a instalação automaticamente, abra o menu do
          navegador e escolha a opção de instalar este aplicativo.
        </Typography>
      </ConfirmationModal>

      <AlertDialog
        title={i18n.t('login.accessDeniedModalTitle')}
        open={loginAccessWarningModalOpen}
        onClose={() => setLoginAccessWarningModalOpen(false)}
        buttonLabel="OK"
      >
        <Typography variant="body2" className={classes.description}>
          {loginAccessWarningErrorCode &&
            i18n.t(`backendErrors.${loginAccessWarningErrorCode}`)}
        </Typography>
      </AlertDialog>
    </div>
  )
}

export default Login
